import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  ReactNode,
} from "react";
import { useLocation } from "react-router";
import { SessionExpiredDialog } from "@/components/common/SessionExpiredDialog";
import { isPublicAuthPage } from "@/constants/authPaths";
import { useAuth } from "./AuthProvider";

interface SessionExpiryContextType {
  showSessionExpired: () => void;
  isShowingDialog: boolean;
}

const SessionExpiryContext = createContext<
  SessionExpiryContextType | undefined
>(undefined);

export const SessionExpiryProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const location = useLocation();
  const { logout } = useAuth();

  // Memoize auth page check
  const isOnAuthPage = useMemo(() => {
    return isPublicAuthPage(location.pathname);
  }, [location.pathname]);

  const showSessionExpired = useCallback(() => {
    if (!isOnAuthPage) {
      setShowDialog(true);
    }
  }, [isOnAuthPage]);

  const handleDialogConfirm = useCallback(() => {
    setShowDialog(false);
    logout();
  }, [logout]);

  return (
    <SessionExpiryContext.Provider
      value={{ showSessionExpired, isShowingDialog: showDialog }}
    >
      {children}
      {showDialog && !isOnAuthPage && (
        <SessionExpiredDialog onConfirm={handleDialogConfirm} />
      )}
    </SessionExpiryContext.Provider>
  );
};

export const useSessionExpiry = () => {
  const context = useContext(SessionExpiryContext);
  if (context === undefined) {
    throw new Error(
      "useSessionExpiry must be used within a SessionExpiryProvider",
    );
  }
  return context;
};
